import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('avipork_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors - limpiar completamente y redirigir
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado o inválido
      const isLoginPage = window.location.pathname === '/login';
      
      if (!isLoginPage) {
        console.log('[API] Error 401 detectado, limpiando auth y redirigiendo...');
        // Limpiar todos los tokens
        localStorage.removeItem('avipork_token');
        localStorage.removeItem('avipork_user');
        localStorage.removeItem('avipork_supabase_auth');
        // Limpiar claves de Supabase
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sb-') || key.includes('supabase')) {
            localStorage.removeItem(key);
          }
        });
        // Redirigir al login
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Raw Materials
export const materialsAPI = {
  getAll: () => api.get('/materials'),
  create: (data) => api.post('/materials', data),
  update: (id, data) => api.put(`/materials/${id}`, data),
  delete: (id) => api.delete(`/materials/${id}`),
};

// Inventory Movements
export const inventoryAPI = {
  getMovements: (materialId) => api.get('/inventory/movements', { params: { material_id: materialId } }),
  createMovement: (data) => api.post('/inventory/movements', data),
  updateMovement: (id, data) => api.put(`/inventory/movements/${id}`, data),
  deleteMovement: (id) => api.delete(`/inventory/movements/${id}`),
};

// Unit Conversions
export const unitsAPI = {
  getConversions: () => api.get('/units/conversions'),
  convert: (fromUnit, toUnit, quantity) => api.post(`/units/convert?from_unit=${fromUnit}&to_unit=${toUnit}&quantity=${quantity}`),
};

// Formulas
export const formulasAPI = {
  getAll: () => api.get('/formulas'),
  create: (data) => api.post('/formulas', data),
  update: (id, data) => api.put(`/formulas/${id}`, data),
  delete: (id) => api.delete(`/formulas/${id}`),
};

// Production Orders
export const productionAPI = {
  getAll: () => api.get('/production'),
  create: (data) => api.post('/production', data),
};

// Clients (mantener para compatibilidad)
export const clientsAPI = {
  getAll: () => api.get('/clients'),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Projects (Proyectos para despachos internos)
export const projectsAPI = {
  getAll: (status) => api.get('/projects', { params: status ? { status } : {} }),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
};

// Dispatches (Despachos)
export const dispatchesAPI = {
  getAll: (filters) => api.get('/dispatches', { params: filters }),
  getById: (id) => api.get(`/dispatches/${id}`),
  create: (data) => api.post('/dispatches', data),
  update: (id, data) => api.put(`/dispatches/${id}`, data),
  delete: (id) => api.delete(`/dispatches/${id}`),
  getSummaryByLote: (lote) => api.get('/dispatches/summary/by-lote', { params: { lote_number: lote } }),
};

// Suppliers (Proveedores)
export const suppliersAPI = {
  getAll: () => api.get('/suppliers'),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

// Purchase Orders (Órdenes de Compra)
export const purchaseOrdersAPI = {
  getAll: (status) => api.get('/purchase-orders', { params: status ? { status } : {} }),
  getById: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  receive: (id) => api.put(`/purchase-orders/${id}/receive`),
  cancel: (id) => api.put(`/purchase-orders/${id}/cancel`),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
};

// Products
export const productsAPI = {
  getAll: () => api.get('/products'),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Medications
export const medicationsAPI = {
  getAll: () => api.get('/medications'),
  create: (data) => api.post('/medications', data),
  update: (id, data) => api.put(`/medications/${id}`, data),
  delete: (id) => api.delete(`/medications/${id}`),
  getByBarcode: (barcode) => api.get(`/medications/barcode/${barcode}`),
  getMovements: (medicationId) => api.get('/medications/movements', { params: { medication_id: medicationId } }),
  createMovement: (data) => api.post('/medications/movements', data),
};

// Invoices
export const invoicesAPI = {
  getAll: () => api.get('/invoices'),
  create: (data) => api.post('/invoices', data),
  updateStatus: (id, status) => api.put(`/invoices/${id}/status?status=${status}`),
  downloadPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
};

// Reports
export const reportsAPI = {
  downloadInventoryExcel: () => api.get('/reports/inventory/excel', { responseType: 'blob' }),
  downloadInventoryPdf: () => api.get('/reports/inventory/pdf', { responseType: 'blob' }),
  downloadDispatchesExcel: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.project_id) params.append('project_id', filters.project_id);
    if (filters.lote_number) params.append('lote_number', filters.lote_number);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    return api.get(`/reports/dispatches/excel?${params.toString()}`, { responseType: 'blob' });
  },
  downloadDispatchesPdf: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.project_id) params.append('project_id', filters.project_id);
    if (filters.lote_number) params.append('lote_number', filters.lote_number);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    return api.get(`/reports/dispatches/pdf?${params.toString()}`, { responseType: 'blob' });
  },
};

// Settings
export const settingsAPI = {
  getExchangeRate: () => api.get('/settings/exchange-rate'),
  updateExchangeRate: (rate) => api.put('/settings/exchange-rate', { rate }),
  getOperationalCost: () => api.get('/settings/operational-cost'),
  updateOperationalCost: (cost_per_qq) => api.put('/settings/operational-cost', { cost_per_qq }),
};

// Operational Costs (Costos Operativos)
export const operationalCostsAPI = {
  // Employees
  getEmployees: (status) => api.get('/employees', { params: status ? { status } : {} }),
  createEmployee: (data) => api.post('/employees', data),
  updateEmployee: (id, data) => api.put(`/employees/${id}`, data),
  deleteEmployee: (id) => api.delete(`/employees/${id}`),
  
  // Payroll
  getPayroll: (period, employeeId) => api.get('/payroll', { params: { period, employee_id: employeeId } }),
  createPayroll: (data) => api.post('/payroll', data),
  deletePayroll: (id) => api.delete(`/payroll/${id}`),
  
  // Electricity Expenses
  getElectricityExpenses: (period) => api.get('/electricity-expenses', { params: period ? { period } : {} }),
  createElectricityExpense: (data) => api.post('/electricity-expenses', data),
  updateElectricityExpense: (id, data) => api.put(`/electricity-expenses/${id}`, data),
  deleteElectricityExpense: (id) => api.delete(`/electricity-expenses/${id}`),
  
  // Maintenance Expenses
  getMaintenanceExpenses: (period) => api.get('/maintenance-expenses', { params: period ? { period } : {} }),
  createMaintenanceExpense: (data) => api.post('/maintenance-expenses', data),
  updateMaintenanceExpense: (id, data) => api.put(`/maintenance-expenses/${id}`, data),
  deleteMaintenanceExpense: (id) => api.delete(`/maintenance-expenses/${id}`),
  
  // Gasoil Expenses
  getGasoilExpenses: (period) => api.get('/gasoil-expenses', { params: period ? { period } : {} }),
  createGasoilExpense: (data) => api.post('/gasoil-expenses', data),
  updateGasoilExpense: (id, data) => api.put(`/gasoil-expenses/${id}`, data),
  deleteGasoilExpense: (id) => api.delete(`/gasoil-expenses/${id}`),
  
  // Cost Summary
  getCostSummary: (period) => api.get('/operational-costs/summary', { params: { period } }),
};

export default api;
